import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { linkEnds, linkPath, visibleIn, type LinkPoint, type LinkRect } from "./desk-links.js";
import { studyMotion } from "../ui/motion.js";

/**
 * THE DRAWN CONNECTION (2026-10-31, Andre, from the arrange-files ideation,
 * chunks 12 and 13): while a pair of markers is selected, a line between
 * them, over everything and in the way of nothing. One SVG fixed over the
 * page, `pointer-events: none` and hidden from the accessibility tree, so
 * it never intercepts a press, a drag or a scroll and never reads as
 * content: the relationship is already legible from the markers' words.
 *
 * It follows. The ends are measured from the page itself, not kept, so the
 * line is right after a drag of the file, a scroll of the conversation, a
 * page turned, a window resized, a reply still typing: any scroll (captured,
 * so the conversation's own scroller counts), any resize and any change the
 * page makes to itself (a MutationObserver over the body: a dragged paper
 * moves by its style; a turned page by its children) schedules one measure
 * on the next frame. A line is drawn only while both of its ends are on
 * screen; an end that is not, or not rendered at all, draws nothing, and the
 * marker that was pressed offers the way to it instead (home.tsx).
 *
 * THE COMET (2026-11-02, Andre, from the Codex design session; the study
 * only, and never under reduced motion — ui/motion.ts): when a connection
 * OPENS, a bright point with a tail and an echo runs the explanation line
 * from the conversation to the file while the line draws itself in under
 * it, and a small glow pulses where it lands. When the connection CLOSES,
 * the line is kept for one more moment (`data-leaving`) while the comet runs
 * it again and the stroke erases behind it, start to end. Only the
 * connection's opening and closing earn a comet: an end scrolling out of
 * view and back hides and shows the line at once, as it always did. The
 * dashed line to the focus paper has no comet; it fades. Outside the study
 * none of this exists, and a closed line leaves the page the instant it did
 * before.
 */
export interface LinkLine {
  readonly id: string;
  /** CSS selectors for the two ends: the marker or paper to measure. */
  readonly from: string;
  readonly to: string;
  readonly tone: "plain" | "focus";
}

interface DrawnLine {
  readonly id: string;
  readonly d: string;
  readonly tone: "plain" | "focus";
  /** Where the line ends: the comet's landing, the glow's centre. */
  readonly to: LinkPoint;
}

/** How long a closed line is kept to be erased: the comet's run and a breath. */
export const LINGER_MS = 420;
/** How long a newly opened line carries its comet and landing glow before it is an ordinary line. */
const FRESH_MS = 900;

/** An attribute selector for one end, with the value quoted safely. */
export function endSelector(attribute: "data-link-end" | "data-key", value: string): string {
  return `[${attribute}="${value.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"]`;
}

function rectOf(selector: string): LinkRect | null {
  const element = document.querySelector(selector);
  if (element === null) return null;
  const r = element.getBoundingClientRect();
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
}

export function measureLines(lines: readonly LinkLine[], viewport: { readonly width: number; readonly height: number }): readonly DrawnLine[] {
  const drawn: DrawnLine[] = [];
  for (const line of lines) {
    const a = rectOf(line.from);
    const b = rectOf(line.to);
    if (a === null || b === null || !visibleIn(a, viewport) || !visibleIn(b, viewport)) continue;
    const ends = linkEnds(a, b);
    if (ends === null) continue;
    drawn.push({ id: line.id, d: linkPath(ends.from, ends.to), tone: line.tone, to: ends.to });
  }
  return drawn;
}

const same = (a: readonly DrawnLine[], b: readonly DrawnLine[]): boolean => a.length === b.length && a.every((line, i) => line.id === b[i]?.id && line.d === b[i]?.d && line.tone === b[i]?.tone);

/**
 * THE COMET, in SMIL: a motion along the line's own path, begun from code
 * the moment the group is on the page (`beginElementAt`, each piece a few
 * hundredths behind the head), because a `begin` written in the markup is a
 * time on the SVG's clock, which has long passed for a line opened while the
 * overlay already stood. The group fades at the end of the run (app.css), so
 * nothing is left but the line. Where the browser has no SMIL clock (jsdom),
 * the circles simply sit at the origin, unseen.
 */
const COMET_PIECES: readonly { readonly className: string; readonly r: number; readonly opacity: number; readonly behind: number }[] = [
  { className: "pcs-comet-tail", r: 0.8, opacity: 0.25, behind: 0.09 },
  { className: "pcs-comet-tail", r: 1.2, opacity: 0.4, behind: 0.06 },
  { className: "pcs-comet-tail", r: 1.6, opacity: 0.6, behind: 0.03 },
  { className: "pcs-comet-echo", r: 2.2, opacity: 0.35, behind: 0.08 },
  { className: "pcs-comet-head", r: 2.2, opacity: 1, behind: 0 },
];

function Comet({ d }: { readonly d: string }): ReactNode {
  const group = useRef<SVGGElement | null>(null);
  useEffect(() => {
    const g = group.current;
    if (g === null) return;
    const motions = g.querySelectorAll("animateMotion");
    motions.forEach((motion, i) => {
      const piece = COMET_PIECES[i];
      const timed = motion as SVGAnimationElement & { beginElementAt?: (offset: number) => void };
      if (piece !== undefined && typeof timed.beginElementAt === "function") timed.beginElementAt(piece.behind);
    });
  }, [d]);
  return (
    <g ref={group} className="pcs-link-comet">
      {COMET_PIECES.map((piece, i) => (
        <circle key={i} className={piece.className} r={piece.r} opacity={piece.opacity}>
          <animateMotion dur="0.36s" begin="indefinite" fill="freeze" path={d} calcMode="spline" keyTimes="0;1" keySplines="0.2 0.8 0.2 1" />
        </circle>
      ))}
    </g>
  );
}

export function DeskLinkOverlay({ lines }: { readonly lines: readonly LinkLine[] }): ReactNode {
  const [drawn, setDrawn] = useState<readonly DrawnLine[]>([]);
  /** Connections opened a moment ago: their lines carry the comet and the landing glow. */
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
  /** Connections closed a moment ago: their last drawn lines, kept to be erased. */
  const [leaving, setLeaving] = useState<readonly DrawnLine[]>([]);
  const lastDrawn = useRef<readonly DrawnLine[]>([]);
  const lastIds = useRef<readonly string[]>([]);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // The lines that are open now against the lines that were: what opened is
  // fresh, what closed lingers — in the study, and only there.
  useLayoutEffect(() => {
    const ids = lines.map((line) => line.id);
    const opened = ids.filter((id) => !lastIds.current.includes(id));
    const closed = lastIds.current.filter((id) => !ids.includes(id));
    lastIds.current = ids;
    if (!studyMotion()) return;
    const later = (ms: number, run: () => void): void => {
      const timer = setTimeout(() => {
        timers.current.delete(timer);
        run();
      }, ms);
      timers.current.add(timer);
    };
    if (opened.length > 0) {
      setFresh((prev) => new Set([...prev, ...opened]));
      later(FRESH_MS, () => {
        setFresh((prev) => {
          const next = new Set(prev);
          for (const id of opened) next.delete(id);
          return next;
        });
      });
    }
    const gone = lastDrawn.current.filter((line) => closed.includes(line.id));
    if (gone.length > 0) {
      setLeaving((prev) => [...prev.filter((line) => !closed.includes(line.id)), ...gone]);
      later(LINGER_MS, () => {
        setLeaving((prev) => prev.filter((line) => !gone.some((g) => g.id === line.id)));
      });
    }
  }, [lines]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  useLayoutEffect(() => {
    if (lines.length === 0) {
      lastDrawn.current = [];
      setDrawn((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    let frame = 0;
    const measure = (): void => {
      const next = measureLines(lines, { width: window.innerWidth, height: window.innerHeight });
      lastDrawn.current = next;
      setDrawn((prev) => (same(prev, next) ? prev : next));
    };
    const schedule = (): void => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style", "class"], childList: true, characterData: true, subtree: true });
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [lines]);

  if (drawn.length === 0 && leaving.length === 0) return null;
  return (
    <svg className="pcs-link-overlay" aria-hidden="true" focusable="false" data-testid="desk-link-overlay">
      {leaving.map((line) => (
        <g key={`leaving:${line.id}`}>
          <path className="pcs-link-line" data-link-line={line.id} data-tone={line.tone} data-leaving="true" d={line.d} pathLength={1} />
          {line.tone === "plain" ? <Comet d={line.d} /> : null}
        </g>
      ))}
      {drawn.map((line) => (
        <g key={line.id}>
          <path className="pcs-link-line" data-link-line={line.id} data-tone={line.tone} d={line.d} pathLength={1} />
          {fresh.has(line.id) && line.tone === "plain" ? (
            <>
              <Comet d={line.d} />
              <circle className="pcs-link-land" cx={line.to.x} cy={line.to.y} r={3} />
            </>
          ) : null}
        </g>
      ))}
    </svg>
  );
}
